import { useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ShieldCheck, Plus, Trash2, Copy, Eye, EyeOff, Share2, Activity, Search, Lock, AlertTriangle, Clock, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import AdvancedTable from "@/components/table/AdvancedTable";

const CATEGORIES = ["login", "api_key", "database", "ssh", "license", "certificate", "other"];
const ENVS = ["production", "staging", "development", "test", "shared"];
const ENV_COLOR = {
  production: "chip-rejected", staging: "chip-pending",
  development: "chip-internal", test: "chip-internal", shared: "chip-status",
};

export default function Vault() {
  const { activeWorkspaceId, activeWorkspace, user, memberships } = useAuth();
  const { can } = useWorkspacePerms();
  const _membership = memberships?.find((m) => m.workspace_id === activeWorkspaceId);
  const _legacyAdmin = ["owner", "admin", "manager", "workspace_owner", "workspace_admin"].includes(_membership?.role) || ["owner", "admin", "manager"].includes(activeWorkspace?.role) || activeWorkspace?.owner_id === user?.id;
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterEnv, setFilterEnv] = useState("");
  const [editing, setEditing] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const isAdmin = _legacyAdmin || can("vault.manage");

  const download = async (fmt, includeSecrets) => {
    try {
      const resp = await api.get(
        `/workspaces/${activeWorkspaceId}/credentials/bulk-export`,
        { params: { format: fmt, include_secrets: includeSecrets ? 1 : 0 },
          responseType: "blob" });
      const dispo = resp.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(dispo);
      const fn = m ? m[1] : `credentials.${fmt}`;
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = fn;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`Exported ${fn}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
  };

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filterCat) params.set("category", filterCat);
      if (filterEnv) params.set("environment", filterEnv);
      if (q) params.set("q", q);
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/credentials?${params}`);
      setRows(data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId, filterCat, filterEnv]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) => (r.name || "").toLowerCase().includes(s) || (r.username || "").toLowerCase().includes(s) || (r.url || "").toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <>
      <PageHeader
        eyebrow="Security · Vault"
        title={<span className="inline-flex items-center gap-2"><Lock className="w-6 h-6" /> Credentials vault</span>}
        description="AES-256-GCM encrypted secrets, shared with departments or individuals. Every reveal and edit is audited with IP, location, device, and timestamp."
        testid="vault-header"
        actions={
          <div className="flex gap-2 flex-wrap">
            {isAdmin && <Button variant="outline" className="rounded-none" onClick={() => setMatrixOpen(true)} data-testid="vault-matrix-btn"><ShieldCheck className="w-4 h-4 mr-2" /> Access matrix</Button>}
            {isAdmin && <Button variant="outline" className="rounded-none" onClick={() => setLogOpen(true)} data-testid="vault-log-btn"><Activity className="w-4 h-4 mr-2" /> Audit log</Button>}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-none" data-testid="vault-export-btn">
                    <Download className="w-4 h-4 mr-2" /> Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">Metadata only</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => download("csv", false)} data-testid="vault-export-csv">CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => download("xlsx", false)} data-testid="vault-export-xlsx">Excel (XLSX)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => download("sql", false)} data-testid="vault-export-sql">SQL dump</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => download("json", false)} data-testid="vault-export-json">JSON</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-red-600">With plaintext passwords · audited</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => download("csv", true)} data-testid="vault-export-csv-secrets">CSV + secrets</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => download("xlsx", true)} data-testid="vault-export-xlsx-secrets">Excel + secrets</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => download("sql", true)} data-testid="vault-export-sql-secrets">SQL + secrets</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {isAdmin && <Button variant="outline" className="rounded-none" onClick={() => setImportOpen(true)} data-testid="vault-import-btn"><Upload className="w-4 h-4 mr-2" /> Import</Button>}
            {isAdmin && <Button className="rounded-none" onClick={() => setEditing({})} data-testid="vault-new-btn"><Plus className="w-4 h-4 mr-2" /> Add credential</Button>}
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10">
        <div className="border border-border bg-muted/40 p-3 flex items-center gap-2 flex-wrap" data-testid="vault-filters">
          <Search className="w-3.5 h-3.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, username, URL…" className="h-8 w-72 text-xs" data-testid="vault-q" />
          <Select value={filterCat || "_all"} onValueChange={(v) => setFilterCat(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="vault-filter-cat"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterEnv || "_all"} onValueChange={(v) => setFilterEnv(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="vault-filter-env"><SelectValue placeholder="Environment" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All envs</SelectItem>
              {ENVS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-[11px] ml-auto">{filtered.length} of {rows.length}</span>
        </div>
      </div>
      <div className="px-4 sm:px-6 md:px-10 py-6">
        {filtered.length === 0 && !busy ? (
          <EmptyState
            title="No credentials yet"
            description="Add encrypted credentials and share them with departments or individuals. Plaintext is never persisted unencrypted."
            icon={Lock}
            testid="vault-empty"
            action={isAdmin && <Button className="rounded-none" onClick={() => setEditing({})} data-testid="vault-empty-cta">Add first credential</Button>}
          />
        ) : (
          <AdvancedTable
            tableKey="vault-credentials"
            title="Credentials"
            rows={filtered}
            columns={[
              {
                key: "name", label: "Name", width: 260, sortable: true, filterType: "text",
                accessor: (c) => c.name || "",
                render: (c) => (
                  <button onClick={() => setDetailFor(c)} className="text-left font-semibold min-w-0 truncate hover:underline">
                    {c.name}
                  </button>
                ),
              },
              {
                key: "category", label: "Category", width: 130, sortable: true, filterType: "enum",
                filterOptions: CATEGORIES, accessor: (c) => c.category || "",
                render: (c) => <span className="chip text-[10px]">{c.category}</span>,
              },
              {
                key: "environment", label: "Env", width: 130, sortable: true, filterType: "enum",
                filterOptions: ENVS, accessor: (c) => c.environment || "",
                render: (c) => <span className={`chip text-[10px] ${ENV_COLOR[c.environment] || ""}`}>{c.environment}</span>,
              },
              {
                key: "service", label: "Service", width: 140, sortable: true, filterType: "text",
                accessor: (c) => c.service_label || c.service || "",
                render: (c) => (c.service_label || c.service)
                  ? <span className="chip text-[10px]" data-testid={`vault-row-service-${c.id}`}>{c.service_label || c.service}</span>
                  : <span className="text-[11px] text-muted-foreground">—</span>,
              },
              {
                key: "username", label: "Username", width: 180, sortable: true, filterType: "text",
                accessor: (c) => c.username || "",
                render: (c) => <span className="font-mono text-xs truncate">{c.username || "—"}</span>,
              },
              {
                key: "password", label: "Password", width: 190, sortable: false,
                render: (c) => <MaskedPasswordCell cred={c} onChanged={load} />,
                exportValue: () => "••••••••",
              },
              {
                key: "url", label: "URL", width: 220, sortable: true, filterType: "text",
                accessor: (c) => c.url || "",
                render: (c) => c.url ? <a href={c.url} target="_blank" rel="noreferrer" className="underline text-xs" onClick={(e) => e.stopPropagation()}>{c.url.replace(/^https?:\/\//, "")}</a> : "—",
              },
              {
                key: "last_password_change_at", label: "Last rotation", width: 160, sortable: true, filterType: "date-range",
                accessor: (c) => c.last_password_change_at || "",
                render: (c) => c.last_password_change_at ? <RotationAge ts={c.last_password_change_at} /> : "—",
              },
            ]}
            loading={busy && rows.length === 0}
            emptyIcon={Lock}
            emptyTitle="No credentials match"
            emptyDescription="Try changing filters or search."
            onRowClick={(c) => setDetailFor(c)}
            selectable={isAdmin}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            renderBulkBar={(rows) => (
              <div className="flex items-center gap-2 flex-wrap" data-testid="vault-bulk-bar">
                <span className="text-xs">{rows.length} selected</span>
                <Button size="sm" variant="destructive" className="rounded-none h-8"
                        onClick={() => setBulkConfirm(true)}
                        data-testid="vault-bulk-delete-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete selected
                </Button>
                <Button size="sm" variant="outline" className="rounded-none h-8"
                        onClick={() => setSelectedIds(new Set())}
                        data-testid="vault-bulk-clear">
                  Clear
                </Button>
              </div>
            )}
            renderRowActions={(c) => (
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={(e) => { e.stopPropagation(); setDetailFor(c); }} data-testid={`vault-view-${c.id}`}>
                <Eye className="w-3.5 h-3.5" />
              </Button>
            )}
          />
        )}
      </div>

      {editing && isAdmin && <CredentialDialog wsId={activeWorkspaceId} cred={editing} onClose={() => setEditing(null)} onSaved={async () => { await load(); setEditing(null); }} />}
      {detailFor && <CredentialDetail cred={detailFor} isAdmin={isAdmin} onClose={() => setDetailFor(null)} onChanged={load} onEdit={(c) => { setDetailFor(null); setEditing(c); }} />}
      {matrixOpen && <AccessMatrixDialog wsId={activeWorkspaceId} onClose={() => setMatrixOpen(false)} />}
      {logOpen && <WorkspaceAuditDialog wsId={activeWorkspaceId} onClose={() => setLogOpen(false)} />}
      {importOpen && <BulkImportDialog wsId={activeWorkspaceId} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent data-testid="vault-bulk-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} credential{selectedIds.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              These credentials will be soft-deleted (moved to the audit trail). Any active grants will be revoked. Only rows you own or can manage will actually be removed — the server enforces per-row permissions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="vault-bulk-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="vault-bulk-confirm-ok"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/workspaces/${activeWorkspaceId}/credentials/bulk-delete`, { ids: [...selectedIds] });
                  toast.success(`${data.deleted} deleted${data.denied?.length ? ` · ${data.denied.length} denied (permissions)` : ""}`);
                  setSelectedIds(new Set());
                  setBulkConfirm(false);
                  load();
                } catch (e) {
                  toast.error(apiErrorMessage(e, "Bulk delete failed"));
                }
              }}
            >Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function MaskedPasswordCell({ cred, onChanged }) {
  const [shown, setShown] = useState(null);   // string once revealed
  const [busy, setBusy] = useState(false);

  const reveal = async (e) => {
    e.stopPropagation();
    if (shown) { setShown(null); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/credentials/${cred.id}/reveal`);
      setShown(data.password || "");
    } catch (err) { toast.error(apiErrorMessage(err, "Reveal denied")); }
    finally { setBusy(false); }
  };

  const copy = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      // Backend logs `copy` as its own audit event
      const { data } = await api.post(`/credentials/${cred.id}/copy?field=password`);
      try { await navigator.clipboard.writeText(data.value || ""); }
      catch { toast.error("Clipboard blocked by browser"); return; }
      toast.success("Password copied · logged");
      onChanged?.();
    } catch (err) { toast.error(apiErrorMessage(err, "Copy denied")); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-1.5" data-testid={`vault-masked-cell-${cred.id}`} onClick={(e) => e.stopPropagation()}>
      <code className="font-mono text-xs tabular-nums select-all min-w-0 truncate">
        {shown ?? "••••••••••••"}
      </code>
      <button type="button" onClick={reveal} disabled={busy}
              className="opacity-60 hover:opacity-100 hover:text-primary p-0.5"
              title={shown ? "Hide" : "Reveal (logged)"}
              data-testid={`vault-masked-reveal-${cred.id}`}>
        {shown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
      <button type="button" onClick={copy} disabled={busy}
              className="opacity-60 hover:opacity-100 hover:text-primary p-0.5"
              title="Copy to clipboard (logged)"
              data-testid={`vault-masked-copy-${cred.id}`}>
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}


function RotationAge({ ts }) {
  const days = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
  const stale = days > 90;
  return (
    <span className={stale ? "text-red-600 font-semibold inline-flex items-center gap-1" : ""}>
      {stale && <AlertTriangle className="w-3 h-3" />}
      {fmtRelative(ts)} {stale ? "(rotate!)" : ""}
    </span>
  );
}

/* ──────────────────────── Credential edit dialog ─────────────────────── */

function CredentialDialog({ wsId, cred, onClose, onSaved }) {
  const isNew = !cred?.id;
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  // R268tt · Feb 2026 · Service registry — dropdown source of truth.
  const [services, setServices] = useState([]);
  const [showAddSvc, setShowAddSvc] = useState(false);
  const [newSvcLabel, setNewSvcLabel] = useState("");
  const [addingSvc, setAddingSvc] = useState(false);
  const [form, setForm] = useState({
    name: cred.name || "",
    category: cred.category || "login",
    environment: cred.environment || "production",
    service: cred.service_label || cred.service || "",
    client_id: cred.client_id || "",
    project_id: cred.project_id || "",
    url: cred.url || "",
    username: cred.username || "",
    password: "",  // never preload
    rotate_password: !isNew && false,
    two_factor_contacts: (cred.two_factor_contacts || []).join(", "),
    notes: cred.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const loadServices = () =>
    api.get(`/workspaces/${wsId}/vault/services`)
      .then((r) => setServices(r.data || []))
      .catch(() => setServices([]));

  useEffect(() => {
    api.get(`/workspaces/${wsId}/clients`).then((r) => setClients(r.data || [])).catch(() => { });
    api.get(`/workspaces/${wsId}/projects`).then((r) => setProjects(r.data || [])).catch(() => { });
    loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId]);

  const addService = async () => {
    const label = (newSvcLabel || "").trim();
    if (!label) { toast.error("Enter a service name"); return; }
    setAddingSvc(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/vault/services`, { label });
      toast.success(`Added "${data.label}"`);
      await loadServices();
      set("service", data.label);
      setNewSvcLabel(""); setShowAddSvc(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to add service")); }
    finally { setAddingSvc(false); }
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    if (isNew && !form.password) { toast.error("Password required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        category: form.category,
        environment: form.environment,
        service: form.service || null,
        client_id: form.client_id || null,
        project_id: form.project_id || null,
        url: form.url || null,
        username: form.username || null,
        two_factor_contacts: form.two_factor_contacts.split(",").map((x) => x.trim()).filter(Boolean),
        notes: form.notes || null,
      };
      if (isNew) {
        payload.password = form.password;
        await api.post(`/workspaces/${wsId}/credentials`, payload);
        toast.success("Credential added");
      } else {
        if (form.rotate_password && form.password) {
          payload.password = form.password;
        }
        await api.patch(`/credentials/${cred.id}`, payload);
        toast.success("Credential updated");
      }
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="vault-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="w-4 h-4" /> {isNew ? "Add credential" : `Edit · ${cred.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="vault-name" />
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Category" value={form.category} onChange={(v) => set("category", v)} options={CATEGORIES} testid="vault-category" />
            <SelectField label="Environment" value={form.environment} onChange={(v) => set("environment", v)} options={ENVS} testid="vault-environment" />
          </div>
          {/* R268tt · Feb 2026 · Service / provider dropdown with inline
              "Add new service" so admins can extend the list without
              leaving the dialog. */}
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Service / Provider</Label>
            <Select
              value={form.service || "_none"}
              onValueChange={(v) => {
                if (v === "__add_new__") { setShowAddSvc(true); return; }
                set("service", v === "_none" ? "" : v);
              }}
            >
              <SelectTrigger className="h-10" data-testid="vault-service"><SelectValue placeholder="Pick a service…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— None —</SelectItem>
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.label} data-testid={`vault-service-opt-${s.key}`}>
                    {s.label}{!s.is_default && <span className="text-[10px] text-muted-foreground ml-1">(custom)</span>}
                  </SelectItem>
                ))}
                <SelectItem value="__add_new__" data-testid="vault-service-add-new">
                  <span className="inline-flex items-center gap-1 text-primary font-semibold">
                    <Plus className="w-3 h-3" /> Add new service…
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            {showAddSvc && (
              <div className="mt-2 flex items-center gap-2" data-testid="vault-service-add-row">
                <Input
                  autoFocus
                  value={newSvcLabel}
                  onChange={(e) => setNewSvcLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addService(); } }}
                  placeholder="e.g. DigitalOcean"
                  className="h-9 flex-1"
                  data-testid="vault-service-new-label"
                />
                <Button type="button" size="sm" onClick={addService} disabled={addingSvc}
                  data-testid="vault-service-new-save" className="rounded-none">
                  {addingSvc ? "Adding…" : "Add"}
                </Button>
                <Button type="button" size="sm" variant="ghost"
                  onClick={() => { setShowAddSvc(false); setNewSvcLabel(""); }}
                  className="rounded-none">Cancel</Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Client</Label>
              <Select value={form.client_id || "_none"} onValueChange={(v) => set("client_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10" data-testid="vault-client"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Project</Label>
              <Select value={form.project_id || "_none"} onValueChange={(v) => set("project_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10" data-testid="vault-project"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {projects.filter((p) => !form.client_id || p.client_id === form.client_id).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Field label="URL" value={form.url} onChange={(v) => set("url", v)} testid="vault-url" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Username" value={form.username} onChange={(v) => set("username", v)} testid="vault-username" />
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">
                {isNew ? "Password *" : "Set new password (optional)"}
              </Label>
              <div className="relative">
                <Input type={showPass ? "text" : "password"} value={form.password} onChange={(e) => set("password", e.target.value)} className="h-10 pr-10 font-mono" data-testid="vault-password" placeholder={isNew ? "" : "Leave blank to keep current"} />
                <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2" data-testid="vault-password-toggle">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {!isNew && form.password && <p className="text-[10px] text-amber-600 mt-1">Saving will rotate the password and log the change.</p>}
            </div>
          </div>
          <Field label="2-factor contacts (comma-separated emails/phones)" value={form.two_factor_contacts} onChange={(v) => set("two_factor_contacts", v)} testid="vault-2fa" />
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="Recovery info, rotation schedule, etc." data-testid="vault-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="vault-save">{busy ? "Saving…" : isNew ? "Add credential" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────── Credential detail (reveal + grants + log) ─────────────────────── */

function CredentialDetail({ cred, isAdmin, onClose, onChanged, onEdit }) {
  const [tab, setTab] = useState("info");
  const [revealed, setRevealed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [grants, setGrants] = useState([]);
  const [log, setLog] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [members, setMembers] = useState([]);
  const [shareForm, setShareForm] = useState({ target_type: "user", target_id: "", permission: "reveal", expires_at: "" });

  useEffect(() => {
    if (tab === "grants" && isAdmin) {
      api.get(`/credentials/${cred.id}/grants`).then((r) => setGrants(r.data || [])).catch(() => { });
      api.get(`/workspaces/${cred.workspace_id}/departments`).then((r) => setDepartments(r.data || [])).catch(() => { });
      api.get(`/workspaces/${cred.workspace_id}/members`).then((r) => setMembers(r.data || [])).catch(() => { });
    }
    if (tab === "log" && isAdmin) {
      api.get(`/credentials/${cred.id}/access-log`).then((r) => setLog(r.data || [])).catch(() => { });
    }
  }, [tab, cred.id, cred.workspace_id, isAdmin]);

  const reveal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/credentials/${cred.id}/reveal`);
      setRevealed(data.password);
      // Auto-hide after 30s
      setTimeout(() => setRevealed(null), 30000);
    } catch (e) { toast.error(apiErrorMessage(e, "Reveal denied")); }
    finally { setBusy(false); }
  };
  const copy = async (text, label = "Copied") => {
    try { await navigator.clipboard.writeText(text); toast.success(label); }
    catch { toast.error("Couldn't copy"); }
  };
  const del = async () => {
    if (!confirm(`Delete credential "${cred.name}"? This is logged.`)) return;
    try { await api.delete(`/credentials/${cred.id}`); toast.success("Deleted"); onChanged?.(); onClose(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  const addGrant = async () => {
    if (!shareForm.target_id) { toast.error("Pick a target"); return; }
    try {
      await api.post(`/credentials/${cred.id}/grants`, { ...shareForm, expires_at: shareForm.expires_at || null });
      toast.success("Shared");
      setShareForm({ target_type: shareForm.target_type, target_id: "", permission: "reveal", expires_at: "" });
      const { data } = await api.get(`/credentials/${cred.id}/grants`);
      setGrants(data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Share failed")); }
  };
  const revoke = async (g) => {
    if (!confirm(`Revoke ${g.target_type} access for ${g.target_name}?`)) return;
    try {
      await api.delete(`/credentials/${cred.id}/grants/${g.id}`);
      toast.success("Revoked");
      setGrants((rows) => rows.filter((x) => x.id !== g.id));
    } catch (e) { toast.error(apiErrorMessage(e, "Revoke failed")); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="vault-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="w-4 h-4" /> {cred.name}</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 border-b border-border mb-3">
          <TabBtn active={tab === "info"} onClick={() => setTab("info")} testid="vault-tab-info">Details</TabBtn>
          {isAdmin && <TabBtn active={tab === "grants"} onClick={() => setTab("grants")} testid="vault-tab-grants"><Share2 className="w-3 h-3 mr-1" /> Sharing ({grants.length})</TabBtn>}
          {isAdmin && <TabBtn active={tab === "log"} onClick={() => setTab("log")} testid="vault-tab-log"><Activity className="w-3 h-3 mr-1" /> Audit log</TabBtn>}
        </div>
        {tab === "info" && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <KV label="Category"><span className="chip text-[10px]">{cred.category}</span></KV>
              <KV label="Environment"><span className={`chip text-[10px] ${ENV_COLOR[cred.environment]}`}>{cred.environment}</span></KV>
              <KV label="URL">{cred.url ? <a href={cred.url} target="_blank" rel="noreferrer" className="underline break-all">{cred.url}</a> : "—"}</KV>
              <KV label="Username">
                <span className="font-mono inline-flex items-center gap-1">{cred.username || "—"}
                  {cred.username && <button onClick={() => copy(cred.username, "Username copied")} data-testid="vault-copy-username"><Copy className="w-3 h-3" /></button>}
                </span>
              </KV>
              <KV label="Last rotated">{cred.last_password_change_at ? <RotationAge ts={cred.last_password_change_at} /> : "—"}</KV>
              <KV label="2-factor contacts">{(cred.two_factor_contacts || []).join(", ") || "—"}</KV>
            </div>
            <div className="border border-border p-3" data-testid="vault-reveal-card">
              <div className="label-eyebrow mb-2 flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Password (encrypted at rest)</div>
              {revealed ? (
                <div className="flex items-center gap-2">
                  <code className="font-mono text-base bg-amber-500/10 px-3 py-2 flex-1 break-all" data-testid="vault-revealed">{revealed}</code>
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => copy(revealed, "Password copied — clear your clipboard after use")} data-testid="vault-copy-password"><Copy className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setRevealed(null)} data-testid="vault-hide"><EyeOff className="w-3.5 h-3.5" /></Button>
                  <span className="text-[10px] text-muted-foreground"><Clock className="w-3 h-3 inline" /> auto-hides in 30s</span>
                </div>
              ) : (
                <Button onClick={reveal} disabled={busy} className="rounded-none" data-testid="vault-reveal-btn"><Eye className="w-4 h-4 mr-2" /> {busy ? "Revealing…" : "Reveal password (logged)"}</Button>
              )}
            </div>
            {cred.notes && (
              <div className="border border-border p-3">
                <div className="label-eyebrow mb-2">Notes</div>
                <p className="whitespace-pre-wrap text-sm">{cred.notes}</p>
              </div>
            )}
            <div className="text-xs text-muted-foreground border-t border-border pt-3">
              Created by user {cred.created_by} · {fmtRelative(cred.created_at)} · Updated {fmtRelative(cred.updated_at)}
            </div>
            {isAdmin && (
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" className="rounded-none" onClick={() => onEdit(cred)} data-testid="vault-edit-btn">Edit</Button>
                <Button variant="destructive" className="rounded-none" onClick={del} data-testid="vault-delete-btn"><Trash2 className="w-4 h-4 mr-2" /> Delete</Button>
              </div>
            )}
          </div>
        )}
        {tab === "grants" && (
          <div className="space-y-3">
            <div className="border border-border p-3 space-y-2" data-testid="vault-share-form">
              <div className="label-eyebrow">Share with</div>
              <div className="grid grid-cols-4 gap-2">
                <Select value={shareForm.target_type} onValueChange={(v) => setShareForm({ ...shareForm, target_type: v, target_id: "" })}>
                  <SelectTrigger className="h-9" data-testid="share-target-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Individual</SelectItem>
                    <SelectItem value="department">Department</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={shareForm.target_id} onValueChange={(v) => setShareForm({ ...shareForm, target_id: v })}>
                  <SelectTrigger className="h-9" data-testid="share-target-id"><SelectValue placeholder="Pick…" /></SelectTrigger>
                  <SelectContent>
                    {shareForm.target_type === "user"
                      ? members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email}</SelectItem>)
                      : departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={shareForm.permission} onValueChange={(v) => setShareForm({ ...shareForm, permission: v })}>
                  <SelectTrigger className="h-9" data-testid="share-permission"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">View only (metadata)</SelectItem>
                    <SelectItem value="reveal">Reveal (read password)</SelectItem>
                    <SelectItem value="edit">Edit + reveal</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="date" value={shareForm.expires_at} onChange={(e) => setShareForm({ ...shareForm, expires_at: e.target.value })} className="h-9 text-xs" placeholder="Expires" data-testid="share-expires" />
              </div>
              <Button onClick={addGrant} disabled={!shareForm.target_id} className="rounded-none" data-testid="share-submit"><Share2 className="w-4 h-4 mr-2" /> Grant access</Button>
            </div>
            {grants.length === 0 ? <div className="text-sm text-muted-foreground py-4 text-center">No active grants. Only you (creator) and workspace admins can see this credential.</div>
              : (
                <div className="border border-border" data-testid="vault-grants-table">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
                    <div className="col-span-3">Type</div>
                    <div className="col-span-4">Target</div>
                    <div className="col-span-2">Permission</div>
                    <div className="col-span-2">Expires</div>
                    <div className="col-span-1 text-right">Revoke</div>
                  </div>
                  {grants.map((g) => (
                    <div key={g.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border text-sm items-center" data-testid={`grant-${g.id}`}>
                      <div className="col-span-3"><span className="chip text-[10px]">{g.target_type}</span></div>
                      <div className="col-span-4 truncate">{g.target_name}</div>
                      <div className="col-span-2"><span className="chip text-[10px] chip-internal">{g.permission}</span></div>
                      <div className="col-span-2 text-xs text-muted-foreground">{g.expires_at?.slice(0, 10) || "never"}</div>
                      <div className="col-span-1 text-right">
                        <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => revoke(g)} data-testid={`grant-revoke-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
        {tab === "log" && (
          <div className="border border-border" data-testid="vault-audit-log">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
              <div className="col-span-3">When</div>
              <div className="col-span-2">User</div>
              <div className="col-span-2">Action</div>
              <div className="col-span-2">IP</div>
              <div className="col-span-3">Device · location</div>
            </div>
            {log.length === 0 ? <div className="px-3 py-4 text-sm text-muted-foreground">No access yet.</div>
              : log.map((e) => (
                <div key={e.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border text-xs items-center" data-testid={`audit-${e.id}`}>
                  <div className="col-span-3">{e.created_at?.slice(0, 19).replace("T", " ")}</div>
                  <div className="col-span-2 truncate">{e.user_name}</div>
                  <div className="col-span-2"><span className={`chip text-[10px] ${e.action === "reveal" ? "chip-pending" : e.action === "delete" || e.action === "revoke" ? "chip-rejected" : "chip-internal"}`}>{e.action}</span></div>
                  <div className="col-span-2 font-mono text-[10px] truncate">{e.ip || "—"}</div>
                  <div className="col-span-3 text-[10px] text-muted-foreground truncate" title={e.user_agent}>{[e.city, e.country].filter(Boolean).join(", ")} · {e.user_agent?.split(" ")[0] || ""}</div>
                </div>
              ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return <button onClick={onClick} className={`px-3 py-2 text-sm inline-flex items-center gap-1 border-b-2 ${active ? "border-primary font-bold" : "border-transparent text-muted-foreground"}`} data-testid={testid}>{children}</button>;
}

function AccessMatrixDialog({ wsId, onClose }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/credentials/access-matrix`).then((r) => setRows(r.data || [])).finally(() => setBusy(false));
  }, [wsId]);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto" data-testid="vault-matrix">
        <DialogHeader><DialogTitle><ShieldCheck className="w-4 h-4 inline mr-2" />Workspace access matrix</DialogTitle></DialogHeader>
        {busy ? <div className="text-sm">Loading…</div>
          : rows.length === 0 ? <div className="text-sm text-muted-foreground">No credentials yet.</div>
          : (
            <div className="border border-border">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
                <div className="col-span-3">Credential</div>
                <div className="col-span-2">Env</div>
                <div className="col-span-3">Creator</div>
                <div className="col-span-4">Shared with</div>
              </div>
              {rows.map((r) => (
                <div key={r.credential.id} className="grid grid-cols-12 gap-2 px-3 py-2.5 border-b border-border text-sm" data-testid={`matrix-row-${r.credential.id}`}>
                  <div className="col-span-3 font-semibold truncate">{r.credential.name}</div>
                  <div className="col-span-2"><span className={`chip text-[10px] ${ENV_COLOR[r.credential.environment]}`}>{r.credential.environment}</span></div>
                  <div className="col-span-3 truncate text-xs">{r.creator?.name || "—"}</div>
                  <div className="col-span-4 flex flex-wrap gap-1">
                    {r.grants.length === 0 ? <span className="text-xs text-muted-foreground">— admins only —</span>
                      : r.grants.map((g) => (
                        <span key={g.id} className="chip text-[10px]" title={`${g.permission} · expires ${g.expires_at?.slice(0, 10) || "never"}`}>
                          {g.target_type === "department" ? "🏢" : "👤"} {g.target_name} ({g.permission})
                        </span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

function WorkspaceAuditDialog({ wsId, onClose }) {
  const [rows, setRows] = useState([]);
  const [action, setAction] = useState("");
  useEffect(() => {
    api.get(`/workspaces/${wsId}/credentials/access-log${action ? `?action=${action}` : ""}`).then((r) => setRows(r.data || []));
  }, [wsId, action]);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto" data-testid="vault-workspace-log">
        <DialogHeader><DialogTitle><Activity className="w-4 h-4 inline mr-2" />Workspace audit log</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 mb-3">
          <Label className="text-xs">Filter action</Label>
          <Select value={action || "_all"} onValueChange={(v) => setAction(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="ws-log-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All actions</SelectItem>
              {["create", "view", "reveal", "edit", "delete", "share", "revoke"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-[11px] ml-auto">{rows.length} entries</span>
        </div>
        <div className="border border-border">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
            <div className="col-span-3">When</div>
            <div className="col-span-2">User</div>
            <div className="col-span-2">Action</div>
            <div className="col-span-3">Credential</div>
            <div className="col-span-2">IP</div>
          </div>
          {rows.map((e) => (
            <div key={e.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border text-xs items-center" data-testid={`ws-log-row-${e.id}`}>
              <div className="col-span-3">{e.created_at?.slice(0, 19).replace("T", " ")}</div>
              <div className="col-span-2 truncate">{e.user_name}</div>
              <div className="col-span-2"><span className={`chip text-[10px] ${e.action === "reveal" ? "chip-pending" : e.action === "delete" || e.action === "revoke" ? "chip-rejected" : "chip-internal"}`}>{e.action}</span></div>
              <div className="col-span-3 truncate">{e.credential_name}</div>
              <div className="col-span-2 font-mono text-[10px] truncate">{e.ip || "—"}</div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={testid} />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
function KV({ label, children }) {
  return (
    <div>
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className="text-sm mt-0.5">{children}</div>
    </div>
  );
}


/* ──────── R268tt · Feb 2026 · Bulk import dialog ──────── */

function BulkImportDialog({ wsId, onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseCsv = (text) => {
    // Minimal CSV parser (handles quoted fields, commas, escaped quotes,
    // and the UTF-8 BOM our export sets). Good enough for a well-formed
    // round-trip; users with exotic edge cases can use JSON.
    const clean = text.replace(/^\ufeff/, "");
    const out = [];
    let field = ""; let row = []; let inQ = false;
    for (let i = 0; i < clean.length; i++) {
      const ch = clean[i];
      if (inQ) {
        if (ch === '"' && clean[i + 1] === '"') { field += '"'; i++; }
        else if (ch === '"') { inQ = false; }
        else { field += ch; }
      } else {
        if (ch === '"') inQ = true;
        else if (ch === ",") { row.push(field); field = ""; }
        else if (ch === "\r") { /* skip */ }
        else if (ch === "\n") { row.push(field); out.push(row); row = []; field = ""; }
        else field += ch;
      }
    }
    if (field.length || row.length) { row.push(field); out.push(row); }
    if (out.length < 2) return [];
    const headers = out[0].map((h) => (h || "").trim().toLowerCase());
    return out.slice(1).filter((r) => r.some((c) => (c || "").trim() !== ""))
      .map((r) => Object.fromEntries(r.map((v, i) => [headers[i], v])));
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    try {
      if (f.name.toLowerCase().endsWith(".json")) {
        const t = await f.text();
        const arr = JSON.parse(t);
        setRows(Array.isArray(arr) ? arr : []);
      } else {
        const t = await f.text();
        setRows(parseCsv(t));
      }
    } catch (err) {
      toast.error("Couldn't parse file — please use CSV or JSON exported from Vault");
      setRows([]);
    }
  };

  const upload = async () => {
    if (!rows.length) { toast.error("Pick a CSV or JSON file first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/credentials/bulk-import`,
        { rows });
      setResult(data);
      toast.success(`Imported: ${data.created} created, ${data.updated} updated`);
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) (result ? onDone() : onClose()); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-auto" data-testid="vault-import-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2"><Upload className="w-4 h-4" /> Bulk import credentials</DialogTitle>
          <DialogDescription>
            Upload a CSV or JSON file matching the Vault export format. Rows are
            matched by <code className="text-[10px]">id</code> or
            <code className="text-[10px] mx-1">(name, environment)</code> — existing
            entries are updated, new rows are created. Missing services are
            auto-registered.
          </DialogDescription>
        </DialogHeader>
        {!result && (
          <div className="space-y-3">
            <input type="file" accept=".csv,text/csv,.json,application/json" onChange={onFile} className="text-sm" data-testid="vault-import-file" />
            {fileName && (
              <div className="text-xs text-muted-foreground" data-testid="vault-import-file-summary">
                <b>{fileName}</b> — {rows.length} row{rows.length === 1 ? "" : "s"} ready
              </div>
            )}
            {rows.length > 0 && (
              <div className="border border-border p-3 text-xs bg-muted/30 max-h-48 overflow-auto" data-testid="vault-import-preview">
                <div className="font-semibold mb-1">Preview (first 3):</div>
                {rows.slice(0, 3).map((r, i) => (
                  <div key={i} className="mb-1 font-mono">
                    · {(r.name || "").toString().slice(0, 40)} — {r.category || "?"} / {r.environment || "?"} / {r.service_label || r.service || "—"}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {result && (
          <div className="space-y-2 text-sm" data-testid="vault-import-result">
            <div className="border border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/20 p-3">
              ✅ Created: <b>{result.created}</b> · Updated: <b>{result.updated}</b>
              {result.skipped ? <> · Skipped: <b>{result.skipped}</b></> : null}
            </div>
            {result.errors?.length > 0 && (
              <div className="border border-red-500/40 bg-red-50 dark:bg-red-950/20 p-3 text-xs">
                <div className="font-semibold mb-1">Errors ({result.errors.length}):</div>
                {result.errors.slice(0, 10).map((e, i) => (
                  <div key={i}>· Row {e.row}: {e.reason}</div>
                ))}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {!result && <>
            <Button variant="ghost" onClick={onClose} className="rounded-none">Cancel</Button>
            <Button onClick={upload} disabled={busy || rows.length === 0} className="rounded-none" data-testid="vault-import-run">
              {busy ? "Importing…" : `Import ${rows.length} row${rows.length === 1 ? "" : "s"}`}
            </Button>
          </>}
          {result && <Button onClick={onDone} className="rounded-none" data-testid="vault-import-close">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
